// /reports/tally-export — Tally Prime Export (PAGES.md §3.94b)

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { Badge, Button, Checkbox, Input, RadioGroup, Alert, useToast } from '@bbpos/ui';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { TallyExportHistory, TallyExportRow, TallyExportStatus } from '@/features/reports';
import { useTaxRegime } from '@/hooks/use-tax-regime';

type Row = TallyExportRow & { id: string };

// ─── Status → Badge variant mapping (using StatusKey from @bbpos/ui) ─────────

const STATUS_VARIANT: Record<TallyExportStatus, 'queued' | 'inProgress' | 'success' | 'danger'> = {
  QUEUED: 'queued',
  PROCESSING: 'inProgress',
  COMPLETED: 'success',
  FAILED: 'danger',
};

const STATUS_LABEL: Record<TallyExportStatus, string> = {
  QUEUED: 'Queued',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// The history CSV and the KPI tiles cover EVERY export, not the page on screen
// (QA 8.6.10). 100 is the API's pagination cap.
const HISTORY_PAGE_SIZE = 100;

export async function fetchAllTallyExports(
  fetchPage: (page: number, limit: number) => Promise<TallyExportHistory>,
): Promise<TallyExportRow[]> {
  const first = await fetchPage(1, HISTORY_PAGE_SIZE);
  const rows = [...first.rows];
  const totalPages = Math.ceil(first.total / HISTORY_PAGE_SIZE);
  for (let p = 2; p <= totalPages; p += 1) {
    const next = await fetchPage(p, HISTORY_PAGE_SIZE);
    rows.push(...next.rows);
  }
  return rows;
}

const VOUCHER_TYPES = [
  { id: 'SALES', label: 'Sales Vouchers' },
  { id: 'PURCHASE', label: 'Purchase Vouchers' },
  { id: 'EXPENSE', label: 'Expense Vouchers' },
  { id: 'ROUNDING', label: 'Cash Rounding Journals' },
];

// ─── Table columns ──────────────────────────────────────────────────────────

const columns: ColumnDef<Row>[] = [
  {
    key: 'created_at',
    header: 'Date',
    render: (_v, row) => (
      <span className="text-sm tabular-nums text-neutral-900 dark:text-neutral-100">
        {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
      </span>
    ),
  },
  {
    key: 'format',
    header: 'Format',
    render: (_v, row) => (
      <Badge variant="info" size="sm" dot>{row.format}</Badge>
    ),
  },
  {
    key: 'voucher_types',
    header: 'Vouchers',
    render: (_v, row) => (
      <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
        {row.voucher_types.join(', ')}
      </span>
    ),
  },
  {
    key: 'record_count',
    header: 'Records',
    numeric: true,
    render: (_v, row) => (
      <span className="tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
        {row.record_count.toLocaleString('en-IN')}
      </span>
    ),
  },
  {
    key: 'size_bytes',
    header: 'Size',
    numeric: true,
    render: (_v, row) => (
      <span className="tabular-nums text-neutral-600 dark:text-neutral-300">{formatSize(row.size_bytes)}</span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => (
      <Badge variant={STATUS_VARIANT[row.status]} size="sm" dot>{STATUS_LABEL[row.status]}</Badge>
    ),
  },
  {
    key: 'download_url',
    header: '',
    render: (_v, row) => {
      if (row.status !== 'COMPLETED' || !row.download_url) {
        return <span className="text-neutral-300 dark:text-neutral-600">—</span>;
      }
      return (
        <Button
          variant="secondary"
          size="sm"
          icon={
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          }
          onClick={() => window.open(row.download_url, '_blank', 'noopener')}
          aria-label={`Download ${row.format} export`}
        >
          Download
        </Button>
      );
    },
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

// Tally Prime export is an India-only accounting-integration surface keyed on
// GST mapping (CGST/SGST/IGST, GSTIN, HSN/SAC) — GCC VAT regimes have no Tally
// equivalent, so the page is gated (not relabelled). Currency is immutable per
// tenant ⇒ stable regime, so the redirect never changes mid-mount. The gate is
// a thin wrapper to keep the inner component's hook order unconditional. INR →
// `showTally: true`, byte-identical. Sidebar/nav gating is in router.tsx.
export function TallyExportPage() {
  const tax = useTaxRegime();
  if (!tax.showTally) return <Navigate to="/reports" replace />;
  return <TallyExportPageContent />;
}

function TallyExportPageContent() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const formRef = useRef<HTMLDivElement>(null);

  // Filter state — use shared infrastructure like other report pages
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);

  // Tally-specific controls
  const [format, setFormat] = useState<'XML' | 'CSV'>('XML');
  const [selected, setSelected] = useState<string[]>(['SALES', 'PURCHASE', 'EXPENSE', 'ROUNDING']);
  // Ledgers the cash-rounding journals post to; blank sends the API defaults.
  const [gainLedger, setGainLedger] = useState('');
  const [lossLedger, setLossLedger] = useState('');
  const [page, setPage] = useState(1);

  // ─── Export history query with auto-poll for active jobs (Issue #8) ────────

  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'tally-export', page],
    queryFn: () => reportsApi.getTallyExports(page),
    staleTime: 15_000,
    refetchInterval: (query) => {
      const rows = query.state.data?.rows ?? [];
      const hasActive = rows.some((r) => r.status === 'QUEUED' || r.status === 'PROCESSING');
      return hasActive ? 5_000 : false;
    },
  });

  const allQuery = useQuery({
    queryKey: ['reports', 'tally-export', 'all'],
    queryFn: () => fetchAllTallyExports((p, limit) => reportsApi.getTallyExports(p, limit)),
    staleTime: 15_000,
    refetchInterval: (query) => {
      const all = query.state.data ?? [];
      const hasActive = all.some((r) => r.status === 'QUEUED' || r.status === 'PROCESSING');
      return hasActive ? 5_000 : false;
    },
  });

  // ─── Generate mutation with Toast feedback (Issue #7) ─────────────────────

  const mutation = useMutation({
    mutationFn: () =>
      reportsApi.generateTallyExport({
        from: filter.range.from,
        to: filter.range.to,
        format,
        voucher_types: selected,
        location_id: locationId,
        ...(selected.includes('ROUNDING')
          ? { rounding_gain_ledger: gainLedger, rounding_loss_ledger: lossLedger }
          : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', 'tally-export'] });
      toast({ title: 'Export queued successfully', variant: 'success' });
    },
    onError: (err: Error) => {
      const msg = err.message?.includes('no data')
        ? 'No vouchers found for the selected date range and types.'
        : 'Export generation failed. Please try again.';
      toast({ title: msg, variant: 'error' });
    },
  });

  // ─── Ctrl+Enter keyboard shortcut (Issue #6) ─────────────────────────────

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && selected.length > 0 && !mutation.isPending) {
        e.preventDefault();
        mutation.mutate();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selected, mutation]);

  // ─── Derived data ─────────────────────────────────────────────────────────

  const rows: Row[] = useMemo(
    () => (data?.rows ?? []).map((r) => ({ ...r, id: r.id })),
    [data],
  );

  const allRows: Row[] = useMemo(() => allQuery.data ?? [], [allQuery.data]);
  const completedRows = useMemo(() => allRows.filter((r) => r.status === 'COMPLETED'), [allRows]);

  // ─── KPI computations (Issue #5) — all from the full history ─────────────

  const totalExports = allRows.length;
  const totalRecords = useMemo(
    () => completedRows.reduce((sum, r) => sum + r.record_count, 0),
    [completedRows],
  );
  const lastExportDate = useMemo(() => {
    if (completedRows.length === 0) return '—';
    const sorted = [...completedRows].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
    return new Date(sorted[0].created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  }, [completedRows]);
  const successRate = totalExports > 0
    ? `${((completedRows.length / totalExports) * 100).toFixed(0)}%`
    : '—';

  const toggleType = (id: string) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // ─── Export history CSV / share (Issue #13) ───────────────────────────────

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      downloadCsv(
        `tally-export-history.csv`,
        [
          { header: 'Date', get: (r: Row) => new Date(r.created_at).toLocaleDateString('en-IN') },
          { header: 'Format', get: (r: Row) => r.format },
          { header: 'Voucher Types', get: (r: Row) => r.voucher_types.join(', ') },
          { header: 'Records', get: (r: Row) => r.record_count },
          { header: 'Size', get: (r: Row) => formatSize(r.size_bytes) },
          { header: 'Status', get: (r: Row) => r.status },
        ],
        allRows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(
        fmt,
        'Orio360 Tally Export History',
        `Total exports: ${totalExports} • Records: ${totalRecords.toLocaleString('en-IN')} • Success rate: ${successRate}`,
      );
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <ReportPageShell
      title="Tally Export"
      subtitle="Generate Tally Prime-compatible XML or CSV with full GST mapping for sales, purchase and expense vouchers."
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Reports', href: '/reports' },
        { label: 'Tally Export' },
      ]}
      accent="teal-success"
      headerActions={<ReportExportMenu onExport={handleExport} formats={['csv', 'whatsapp', 'email']} />}
      filterBar={
        <ReportFilterBar
          timeRange={timeRange}
          onTimeRangeChange={setTimeRange}
          locationId={locationId}
          onLocationChange={setLocationId}
          extraFilters={
            <div className="flex items-center gap-3">
              <RadioGroup
                label="Format"
                value={format}
                onChange={(v) => setFormat(v as 'XML' | 'CSV')}
                variant="horizontal"
                options={[
                  { value: 'XML', label: 'XML' },
                  { value: 'CSV', label: 'CSV' },
                ]}
              />
            </div>
          }
        />
      }
    >
      {isLoading || allQuery.isLoading ? (
        <ReportSkeleton kpiCount={4} />
      ) : error || allQuery.error ? (
        <ReportError />
      ) : (
        <>
          {/* KPI Row — Issue #5 */}
          <ReportKpiRow
            tiles={[
              { id: 'total', title: 'Total Exports', value: totalExports.toLocaleString('en-IN'), tone: 'teal', icon: KpiIcons.file },
              { id: 'records', title: 'Records Exported', value: totalRecords.toLocaleString('en-IN'), tone: 'brand', icon: KpiIcons.receipt },
              { id: 'last', title: 'Last Export', value: lastExportDate, tone: 'indigo', icon: KpiIcons.clock },
              { id: 'rate', title: 'Success Rate', value: successRate, tone: 'success', icon: KpiIcons.trendUp },
            ]}
          />

          {/* Export Configuration — Issue #11: Glass Depth section header */}
          <section
            ref={formRef}
            className="rounded-2xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 overflow-hidden animate-slide-up mb-6"
          >
            <header className="border-b border-neutral-100 dark:border-neutral-700/60 px-5 py-4 bg-gradient-to-r from-neutral-50/70 to-transparent dark:from-neutral-800/80">
              <h2 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                <span className="inline-block h-5 w-1 rounded-full bg-gradient-to-b from-teal-500 to-success-500" />
                Export Configuration
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 ml-3.5">
                Select voucher types and generate — exports are processed asynchronously.
              </p>
            </header>

            <div className="p-5 sm:p-6">
              {mutation.isError && (
                <div className="mb-4">
                  <Alert
                    variant="danger"
                    title="Export failed"
                    description="No vouchers found for the selected date range and types, or an error occurred."
                  />
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Voucher types */}
                <div className="rounded-xl bg-teal-50/50 dark:bg-teal-900/10 p-4">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-teal-600 dark:text-teal-300 mb-3">
                    Voucher Types
                  </span>
                  <div className="space-y-2">
                    {VOUCHER_TYPES.map((t) => (
                      <Checkbox
                        key={t.id}
                        checked={selected.includes(t.id)}
                        onChange={() => toggleType(t.id)}
                        label={t.label}
                        size="sm"
                      />
                    ))}
                  </div>
                  {selected.includes('ROUNDING') && (
                    <div className="mt-3 space-y-2 animate-fade-in">
                      <Input
                        size="sm"
                        label="Rounding gain ledger"
                        placeholder="Rounding Gain"
                        value={gainLedger}
                        onChange={setGainLedger}
                      />
                      <Input
                        size="sm"
                        label="Rounding loss ledger"
                        placeholder="Rounding Loss"
                        value={lossLedger}
                        onChange={setLossLedger}
                      />
                    </div>
                  )}
                </div>

                {/* Included data summary */}
                <div className="rounded-xl bg-neutral-50/80 dark:bg-neutral-900/40 p-4">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">
                    Included Data
                  </span>
                  <ul className="space-y-1.5 text-xs text-neutral-600 dark:text-neutral-300">
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-success-500 shrink-0" />
                      Sales invoices with GST breakup
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" />
                      Payment receipts (Cash, UPI, Card)
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-warning-500 shrink-0" />
                      Expense entries with categories
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-danger-500 shrink-0" />
                      Credit notes / returns
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 shrink-0" />
                      HSN/SAC codes &amp; GSTIN mapping
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-violet-500 shrink-0" />
                      Cash rounding gain / loss journals
                    </li>
                  </ul>
                </div>

                {/* GST mapping info */}
                <div className="rounded-xl bg-indigo-50/50 dark:bg-indigo-900/10 p-4">
                  <span className="block text-xs font-semibold uppercase tracking-wider text-indigo-600 dark:text-indigo-300 mb-3">
                    GST Compliance
                  </span>
                  <div className="space-y-2 text-xs text-neutral-600 dark:text-neutral-300">
                    <div className="flex justify-between">
                      <span>CGST / SGST</span>
                      <Badge variant="success" size="sm">Mapped</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>IGST</span>
                      <Badge variant="success" size="sm">Mapped</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>B2B GSTIN</span>
                      <Badge variant="success" size="sm">Included</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax Slabs</span>
                      <Badge variant="info" size="sm">5 slabs</Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-neutral-400 dark:text-neutral-500">
                  <kbd className="rounded bg-neutral-200 dark:bg-neutral-700 px-1.5 py-0.5 text-[10px] font-mono font-semibold text-neutral-600 dark:text-neutral-300">Ctrl+Enter</kbd>
                  {' '}to generate
                </p>
                <Button
                  variant="primary"
                  loading={mutation.isPending}
                  disabled={selected.length === 0}
                  onClick={() => mutation.mutate()}
                  icon={
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  }
                >
                  Generate Export
                </Button>
              </div>
            </div>
          </section>

          {/* Export History Table — hidden on mobile (card list below) */}
          <div className="hidden sm:block">
            <ReportTable
              title="Export History"
              columns={columns}
              data={rows}
              emptyTitle="No Tally exports yet"
              emptyDescription="Generate your first export to integrate with Tally Prime."
              totalCount={data?.total}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>

          {/* Empty state CTA — Issue #14 */}
          {rows.length === 0 && (
            <div className="text-center py-2 mb-6">
              <Button variant="primary" size="sm" onClick={scrollToForm}>
                Generate Export
              </Button>
            </div>
          )}

          {/* Mobile card layout for xs-sm — Issue #15 */}
          {rows.length === 0 && (
            <div className="sm:hidden rounded-2xl bg-white dark:bg-neutral-800/80 shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 p-8 text-center mb-6 animate-slide-up">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-teal-500 to-success-500 flex items-center justify-center text-white">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-neutral-900 dark:text-white mb-1">No Tally exports yet</p>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">Generate your first export to integrate with Tally Prime.</p>
              <Button variant="primary" size="sm" onClick={scrollToForm}>Generate Export</Button>
            </div>
          )}
          {rows.length > 0 && (
            <div className="sm:hidden space-y-3 mb-6">
              {rows.map((row) => (
                <div
                  key={row.id}
                  className="rounded-xl bg-white dark:bg-neutral-800/80 dark:backdrop-blur-xl shadow-md ring-1 ring-neutral-100/80 dark:ring-neutral-700/60 p-4 animate-slide-up"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-neutral-900 dark:text-white tabular-nums">
                      {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                    <Badge variant={STATUS_VARIANT[row.status]} size="sm" dot>{STATUS_LABEL[row.status]}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-3">
                    <div>
                      <span className="block font-medium text-neutral-700 dark:text-neutral-200">{row.format}</span>
                      Format
                    </div>
                    <div>
                      <span className="block font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">{row.record_count.toLocaleString('en-IN')}</span>
                      Records
                    </div>
                    <div>
                      <span className="block font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">{formatSize(row.size_bytes)}</span>
                      Size
                    </div>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">{row.voucher_types.join(', ')}</p>
                  {row.status === 'COMPLETED' && row.download_url && (
                    <Button
                      variant="primary"
                      size="sm"
                      fullWidth
                      icon={
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      }
                      onClick={() => window.open(row.download_url, '_blank', 'noopener')}
                    >
                      Download {row.format}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </ReportPageShell>
  );
}
